import React, { useCallback } from 'react'; //eslint-disable-line
import MaterialTable from '@material-table/core';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { DeploymentModel, DeploymentState } from '../../../../models/deploymentModel';
import { Chip, Tooltip } from '@mui/material';
import PendingIcon from '@mui/icons-material/Pending';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportIcon from '@mui/icons-material/Report';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { useNavigate } from 'react-router-dom';
import { DEPLOYMENTS_PATH } from '../../../../routes';

interface Props {
  readonly deployments: DeploymentModel[];
  readonly isLoading: boolean;
  readonly onViewClick: (id: string) => void;
  readonly onRerunClick: (id: string) => void;
}

const DeploymentStatusChip = (props: { deploymentState: DeploymentState }) => ({
  [DeploymentState.IN_PROGRESS]: (
    <Chip label='IN PROGRESS' color='primary' icon={<PendingIcon />} />
  ),
  [DeploymentState.SUCCESS]: (
    <Chip label='SUCCESS' color='success' icon={<CheckCircleIcon />} />
  ),
  [DeploymentState.FAILURE]: (
    <Chip label='FAILURE' color='error' icon={<CancelIcon />} />
  ),
  [DeploymentState.ERROR]: (
    <Chip label='ERROR' color='error' icon={<ReportIcon />} />
  ),
}[props.deploymentState]);

const DeploymentsTable = (props: Props) => {
  const { onViewClick, onRerunClick, deployments, isLoading } = props;
  const navigate = useNavigate();

  const handleGoToMainDeploymentView = () => {
    navigate(DEPLOYMENTS_PATH);
  };

  const renderCreatedDate = useCallback((deployment: DeploymentModel) => {
    return <>{new Date(deployment.createdDate).toLocaleString()}</>;
  }, []);

  const renderStatus = useCallback((deployment: DeploymentModel) => {
    const { status: { state }, serverReloads } = deployment;
    const serverReloadsWithError = serverReloads.filter(reload => !reload.success);

    return (
      <Stack direction='row' spacing={1}>
        <DeploymentStatusChip deploymentState={state} />
        { serverReloadsWithError.length > 0 && (
          <Tooltip title='Finished with reloading server problems'>
            <AnnouncementIcon color='warning' />
          </Tooltip>
        )}
      </Stack>
    );
  }, []);

  return (
    <>
      <MaterialTable
        title={
          <Stack direction='row' spacing={1} alignItems='center'>
            <IconButton aria-label='back to deployment view' onClick={handleGoToMainDeploymentView}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant='h5'>Deployments</Typography>
          </Stack>
        }
        data={deployments}
        isLoading={isLoading}
        columns={[
          {
            title: "Server",
            field: "serverId",
          },
          {
            title: "Commit ID",
            field: "commitId",
          },
          {
            title: "Author",
            field: "author",
          },
          {
            title: "Date of creation",
            field: "createdDate",
            render: renderCreatedDate,
          },
          {
            title: "Status",
            field: "status.state",
            render: renderStatus,
          },

        ]}
        options={{
          padding: 'dense',
          selection: false,
          pageSizeOptions: [10, 25, 50],
          pageSize: 10,
          actionsColumnIndex: 5,
          headerStyle: {
            fontWeight: 'bold',
          },
        }}
        actions={[
          deploymentRow => ({
            icon: () => <VisibilityIcon color='primary' />,
            tooltip: 'View deployment',
            onClick: () => onViewClick(deploymentRow.id),
          }),
          deploymentRow => ({
            icon: () => <ReplayIcon color='primary' />,
            tooltip: 'Rerun deployment',
            onClick: () => onRerunClick(deploymentRow.id),
          }),
        ]}

        components={{
          Container: (props: any) => <Paper elevation={1} {...props} />
        }}
      />
    </>
  )
};

export default DeploymentsTable;
